import isObject from "lodash/isObject";
import isString from "lodash/isString";

/**
 * Display value for the pipeline "Borrower Entity" column.
 *
 * The pipeline boards are fed by two different backend projections and each one
 * surfaces the borrowing entity under a different key:
 *   - `/underwriting/underwriting_pipeline` → `UnderwritingBaseModel`, computed
 *     top-level `entityName`.
 *   - `/loportal/user_pipeline` → `UnderwritingPipelineModel`, computed
 *     `borrowerEntityName` (and it does NOT project the legacy `borrowerEntity`).
 * Older loans instead carry the standalone `borrowerEntity` dict/string.
 *
 * Binding the column to any single one of those keys leaves the whole column on
 * "N/A" whenever the board happens to be fed by the other endpoint. Both
 * projections DO include the flat `entities[]` array, so resolve from that as
 * the last step — same rule the backend uses (the entity flagged
 * `borrowerEntity: true`, else the conventional index-0 entity).
 */
export const getLoanBorrowerEntityName = (loan, fallback = "N/A") => {
  const legacy = loan?.borrowerEntity;

  // Legacy standalone dict wins when present — it's the only shape that carries
  // the `<entityType>` prefix.
  if (isObject(legacy)) {
    const name =
      legacy?.entityName ||
      loan?.entityName ||
      loan?.borrowerEntityName ||
      resolveFromEntities(loan);
    if (!name) return fallback;
    return legacy?.entityType ? `<${legacy.entityType}> ${name}` : name;
  }

  if (isString(legacy) && legacy) return legacy;

  return (
    loan?.entityName ||
    loan?.borrowerEntityName ||
    resolveFromEntities(loan) ||
    fallback
  );
};

// entities-first model: prefer the entity flagged as the borrowing entity, then
// fall back to the first entity that has a name.
const resolveFromEntities = (loan) => {
  const entities = loan?.entities;
  if (!Array.isArray(entities)) return null;
  const flagged = entities.find((e) => isObject(e) && e?.borrowerEntity);
  if (flagged?.entityName) return flagged.entityName;
  return entities.find((e) => isObject(e) && e?.entityName)?.entityName || null;
};

export default getLoanBorrowerEntityName;
